/* ── Overlay / Backdrop ── */
.overlay {
  position: fixed;
  inset: 0;
  z-index: 9999;
  display: flex;
  justify-content: center;
  padding-top: 12vh;
  background: rgba(0, 0, 0, 0.7);
  backdrop-filter: blur(12px);
  -webkit-backdrop-filter: blur(12px);
  animation: overlayFadeIn 250ms cubic-bezier(0.16, 1, 0.3, 1) forwards;
}

/* Ambient gradient glow behind overlay */
.overlay::before {
  content: '';
  position: absolute;
  top: -20%;
  left: 50%;
  transform: translateX(-50%);
  width: 800px;
  height: 400px;
  background: radial-gradient(
    ellipse at center,
    rgba(178, 75, 243, 0.12) 0%,
    rgba(255, 45, 138, 0.06) 40%,
    transparent 70%
  );
  pointer-events: none;
  animation: glowPulse 4s ease-in-out infinite alternate;
}

/* ── Content container ── */
.container {
  width: 100%;
  max-width: 640px;
  padding: 0 var(--nexus-space-lg);
  display: flex;
  flex-direction: column;
  gap: var(--nexus-space-md);
  animation: containerSlideIn 300ms cubic-bezier(0.16, 1, 0.3, 1) forwards;
  align-self: flex-start;
  position: relative;
  z-index: 1;
}

/* ── Search input ── */
.inputWrapper {
  display: flex;
  align-items: center;
  gap: var(--nexus-space-md);
  padding: var(--nexus-space-md) var(--nexus-space-lg);
  background: var(--nexus-surface);
  border: 1px solid transparent;
  border-radius: var(--nexus-radius-surface);
  position: relative;
  box-shadow:
    0 8px 40px rgba(0, 0, 0, 0.4),
    0 0 0 1px rgba(255, 255, 255, 0.06),
    0 0 80px -20px rgba(178, 75, 243, 0.15);
}

/* Gradient border effect */
.inputWrapper::before {
  content: '';
  position: absolute;
  inset: -1px;
  border-radius: inherit;
  padding: 1px;
  background: linear-gradient(
    135deg,
    rgba(178, 75, 243, 0.5),
    rgba(255, 45, 138, 0.3),
    rgba(178, 75, 243, 0.1),
    transparent 60%
  );
  -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
  -webkit-mask-composite: xor;
  mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
  mask-composite: exclude;
  pointer-events: none;
}

.inputIcon {
  flex-shrink: 0;
  color: var(--nexus-purple);
}

.input {
  flex: 1;
  min-width: 0;
  border: none;
  outline: none;
  background: transparent;
  font-family: var(--nexus-font-sans);
  font-size: var(--nexus-text-xl);
  font-weight: var(--nexus-weight-medium);
  color: var(--nexus-text);
  line-height: var(--nexus-leading-normal);
  caret-color: var(--nexus-purple);
}

.input::placeholder {
  color: var(--nexus-text-muted);
  font-weight: var(--nexus-weight-normal);
}

.escBtn {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: var(--nexus-space-xs);
  padding: var(--nexus-space-xs) var(--nexus-space-sm);
  border: 1px solid var(--nexus-border);
  border-radius: var(--nexus-radius-control);
  background: var(--nexus-overlay-subtle);
  color: var(--nexus-text-muted);
  cursor: pointer;
  font-size: var(--nexus-text-xs);
  transition:
    background var(--nexus-transition-fast),
    color var(--nexus-transition-fast),
    border-color var(--nexus-transition-fast);
}

.escBtn:hover {
  background: var(--nexus-surface-hover);
  color: var(--nexus-text-secondary);
  border-color: rgba(178, 75, 243, 0.4);
}

.escKbd {
  font-family: var(--nexus-font-mono);
  font-size: 0.6875rem;
  line-height: 1;
}

/* ── Results panel ── */
.results {
  background: var(--nexus-surface);
  border: 1px solid var(--nexus-border);
  border-radius: var(--nexus-radius-surface);
  box-shadow:
    0 12px 48px rgba(0, 0, 0, 0.35),
    0 0 0 1px rgba(255, 255, 255, 0.05);
  max-height: 56vh;
  overflow-y: auto;
  padding: var(--nexus-space-sm) 0;
}

/* Custom scrollbar for results */
.results::-webkit-scrollbar {
  width: 6px;
}

.results::-webkit-scrollbar-track {
  background: transparent;
}

.results::-webkit-scrollbar-thumb {
  background: rgba(178, 75, 243, 0.25);
  border-radius: var(--nexus-radius-sm);
}

.results::-webkit-scrollbar-thumb:hover {
  background: rgba(178, 75, 243, 0.4);
}

/* ── Category ── */
.category {
  padding: var(--nexus-space-xs) 0;
}

.category + .category {
  border-top: 1px solid var(--nexus-border);
}

.categoryHeader {
  padding: var(--nexus-space-sm) var(--nexus-space-lg);
  font-family: var(--nexus-font-sans);
  font-size: var(--nexus-text-xs);
  font-weight: var(--nexus-weight-bold);
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: var(--nexus-text-muted);
}

/* ── Result item ── */
.resultItem {
  display: flex;
  align-items: center;
  gap: var(--nexus-space-md);
  width: 100%;
  padding: var(--nexus-space-sm) var(--nexus-space-lg);
  border: none;
  background: transparent;
  cursor: pointer;
  text-align: left;
  position: relative;
  transition:
    background 150ms ease,
    transform 120ms ease;
}

.resultItem::before {
  content: '';
  position: absolute;
  left: 0;
  top: 50%;
  transform: translateY(-50%) scaleY(0);
  width: 3px;
  height: 60%;
  border-radius: 0 2px 2px 0;
  background: linear-gradient(180deg, var(--nexus-purple), var(--nexus-pink));
  transition: transform 150ms ease;
}

.resultItem:hover {
  background: linear-gradient(
    90deg,
    rgba(178, 75, 243, 0.06),
    rgba(255, 45, 138, 0.02),
    transparent
  );
}

.resultItem:hover::before {
  transform: translateY(-50%) scaleY(1);
}

.resultItem:active {
  transform: scale(0.995);
}

.resultItem:focus-visible {
  outline: 2px solid var(--nexus-border-focus);
  outline-offset: -2px;
}

/* ── Logo / avatar ── */
.resultLogo {
  flex-shrink: 0;
  width: 42px;
  height: 42px;
  border-radius: var(--nexus-radius-media);
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(
    135deg,
    rgba(178, 75, 243, 0.12),
    rgba(255, 45, 138, 0.06)
  );
  border: 1px solid rgba(178, 75, 243, 0.15);
  transition: border-color 150ms ease;
}

.resultItem:hover .resultLogo {
  border-color: rgba(178, 75, 243, 0.35);
}

.resultLogoImg {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.resultLogoFallback {
  font-family: var(--nexus-font-sans);
  font-size: var(--nexus-text-base);
  font-weight: var(--nexus-weight-bold);
  background: linear-gradient(135deg, var(--nexus-purple), var(--nexus-pink));
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
}

.resultLogoBrandedFallback {
  font-family: var(--nexus-font-sans);
  font-size: var(--nexus-text-base);
  font-weight: var(--nexus-weight-bold);
  color: #fff;
}

/* ── Result text ── */
.resultInfo {
  flex: 1;
  min-width: 0;
  display: flex;
  flex-direction: column;
  gap: 2px;
}

.resultName {
  font-family: var(--nexus-font-sans);
  font-size: var(--nexus-text-sm);
  font-weight: var(--nexus-weight-semibold);
  color: var(--nexus-text);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  transition: color 150ms ease;
}

.resultItem:hover .resultName {
  color: var(--nexus-purple);
}

.resultDesc {
  font-family: var(--nexus-font-sans);
  font-size: var(--nexus-text-xs);
  color: var(--nexus-text-muted);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* ── Member count ── */
.resultMeta {
  flex-shrink: 0;
  display: flex;
  align-items: center;
  gap: var(--nexus-space-sm);
}

.memberCount {
  font-family: var(--nexus-font-sans);
  font-size: var(--nexus-text-xs);
  color: var(--nexus-text-muted);
  white-space: nowrap;
}

/* ── Type badge ── */
.typeBadge {
  flex-shrink: 0;
  font-family: var(--nexus-font-sans);
  font-size: 0.625rem;
  font-weight: var(--nexus-weight-bold);
  padding: 3px 8px;
  border-radius: var(--nexus-radius-full);
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.badgeDefault {
  background: var(--nexus-overlay-medium);
  color: var(--nexus-text-secondary);
}

.badgeInfo {
  background: rgba(178, 75, 243, 0.12);
  color: var(--nexus-purple);
}

.badgeSuccess {
  background: rgba(34, 197, 94, 0.12);
  color: rgb(74, 222, 128);
}

/* ── Empty / hint states ── */
.empty,
.hint {
  padding: var(--nexus-space-2xl) var(--nexus-space-lg);
  text-align: center;
  font-family: var(--nexus-font-sans);
  font-size: var(--nexus-text-sm);
  color: var(--nexus-text-muted);
}

.hint {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--nexus-space-sm);
}

.hintIcon {
  color: rgba(178, 75, 243, 0.4);
}

/* ── Coming soon ── */
.comingSoon {
  padding: var(--nexus-space-sm) var(--nexus-space-lg);
  font-family: var(--nexus-font-sans);
  font-size: var(--nexus-text-xs);
  color: var(--nexus-text-muted);
  font-style: italic;
  opacity: 0.6;
}

/* ── Animations ── */
@keyframes overlayFadeIn {
  from { opacity: 0; }
  to   { opacity: 1; }
}

@keyframes containerSlideIn {
  from {
    opacity: 0;
    transform: translateY(-20px) scale(0.96);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

@keyframes glowPulse {
  from { opacity: 0.6; }
  to   { opacity: 1; }
}

/* ── Mobile ── */
@media (max-width: 768px) {
  .overlay {
    padding-top: var(--nexus-space-lg);
  }

  .overlay::before {
    width: 100%;
    height: 300px;
  }

  .container {
    padding: 0 var(--nexus-space-md);
    max-width: 100%;
  }

  .input {
    font-size: var(--nexus-text-lg);
  }

  .inputWrapper {
    padding: var(--nexus-space-sm) var(--nexus-space-md);
  }

  .escKbd {
    display: none;
  }

  .results {
    max-height: 70vh;
  }

  .resultItem::before {
    display: none;
  }
}
